import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Switch, FormControlLabel, IconButton,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";
import MainModuleTable from "./MainModuleTable";

const EMPTY_FORM = {
  name: "",
  order: "",
  is_active: true,
  show_in_app: true,
  image_url: "",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    background: "#ffffff",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#016551" },
    "&.Mui-focused fieldset": { borderColor: "#016551", borderWidth: "1.5px" },
  },
};

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#016551" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#016551" },
};

export default function MainModuleConfig() {
  const [moduleData, setModuleData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const fetchModuleData = async () => {
    try {
      setLoading(true);
      const res = await axiosConfig.get("home/home-page-module/");
      if (res.data?.status === "success") {
        setModuleData(res.data.response);
      }
    } catch (error) {
      toast.error("Failed to fetch modules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModuleData();
  }, []);

  const handleOpenDialog = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const handleEditDialog = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name,
      order: row.order,
      is_active: row.is_active,
      show_in_app: row.show_in_app,
      image_url: row.image_url || "",
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggle = (name) => {
    setForm((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Module name is required");
      return;
    }
    const orderNum = Number(form.order);
    if (!form.order || orderNum <= 0) {
      toast.error("Order must be greater than 0");
      return;
    }
    const payload = {
      name: form.name.trim(),
      order: orderNum,
      is_active: form.is_active,
      show_in_app: form.show_in_app,
      ...(form.image_url.trim() && { image_url: form.image_url.trim() }),
    };
    try {
      if (editingId) {
        await axiosConfig.put(`home/home-page-module/${editingId}/`, payload);
        toast.success("Module updated successfully");
      } else {
        await axiosConfig.post("home/home-page-module/", payload);
        toast.success("Module added successfully");
      }
      setDialogOpen(false);
      setEditingId(null);
      fetchModuleData();
    } catch (error) {
      toast.error(editingId ? "Failed to update module" : "Failed to add module");
    }
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          p: 3,
          mb: 3,
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <SettingsIcon sx={{ color: "#ffffff", fontSize: 32 }} />
          <Box>
            <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: "1.25rem" }}>
              Main Module Config
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>
              Manage home page main modules
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            background: "#016551",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 2.5,
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
              boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
            },
          }}
        >
          Add Module
        </Button>
      </Box>

      <MainModuleTable data={moduleData} loading={loading} onEdit={handleEditDialog} />

      {/* Add Module Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            background: "#04343a",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 2,
            px: 3,
          }}
        >
          {editingId ? "Edit Module" : "Add New Module"}
          <IconButton size="small" onClick={handleCloseDialog} sx={{ color: "rgba(255,255,255,0.7)" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 4, px: 3, pb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

            {/* Module Name */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", mb: 0.5 , mt: 1}}>
                Module Name <span style={{ color: "#dc2626" }}>*</span>
              </Typography>
              <TextField
                name="name"
                value={form.name}
                onChange={handleFormChange}
                fullWidth
                size="small"
                placeholder="e.g. Market Overview"
                sx={fieldSx}
              />
            </Box>

            {/* Order */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", mb: 0.5 }}>
                Order <span style={{ color: "#dc2626" }}>*</span>
              </Typography>
              <TextField
                name="order"
                value={form.order}
                onChange={handleFormChange}
                fullWidth
                size="small"
                type="number"
                placeholder="e.g. 1"
                inputProps={{ min: 1 }}
                sx={fieldSx}
              />
            </Box>

            {/* Image URL */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", mb: 0.5 }}>
                Image URL{" "}
                <span style={{ color: "#94a3b8", fontWeight: 400 }}>(optional)</span>
              </Typography>
              <TextField
                name="image_url"
                value={form.image_url}
                onChange={handleFormChange}
                fullWidth
                size="small"
                placeholder="https://example.com/image.png"
                sx={fieldSx}
              />
            </Box>

            {/* Toggles */}
            <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={() => handleToggle("is_active")}
                    sx={switchSx}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1e293b", lineHeight: 1.2 }}>
                      Is Active
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                      {form.is_active ? "Enabled" : "Disabled"}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.show_in_app}
                    onChange={() => handleToggle("show_in_app")}
                    sx={switchSx}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1e293b", lineHeight: 1.2 }}>
                      Show in App
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                      {form.show_in_app ? "Visible" : "Hidden"}
                    </Typography>
                  </Box>
                }
              />
            </Box>

          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              textTransform: "none",
              color: "#64748b",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              px: 2.5,
              "&:hover": { background: "#f8fafc" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 2.5,
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
              },
            }}
          >
            {editingId ? "Update Module" : "Add Module"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
